import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Table, Card, Input, Select, Button, Space, Tag, Typography,
    message, Dropdown, Modal, Drawer, Form, Popconfirm, Descriptions,
    List, Avatar, Divider, Spin, Tooltip, DatePicker, InputNumber,
    Row, Col
} from 'antd';
import {
    SearchOutlined,
    FilterOutlined,
    MoreOutlined,
    EditOutlined,
    IdcardOutlined,
    UserOutlined,
    PlusOutlined,
    DeleteOutlined,
    PhoneOutlined,
    CreditCardOutlined
} from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { participantService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import GroupsDrawer from '../components/GroupsDrawer';
import TagsDrawer from '../components/TagsDrawer';
import { useSoftphone } from '../context/SoftphoneContext';

const { Title, Text } = Typography;
const { Option } = Select;

// Design Token
const PRIMARY_RED = '#DA291C';

// Spanish Validation Messages
/* eslint-disable no-template-curly-in-string */
const validateMessages = {
    required: '${label} es obligatorio',
    types: {
        email: '${label} no es un correo válido',
        number: '${label} no es un número válido',
    },
    number: {
        range: '${label} debe estar entre ${min} y ${max}',
    },
};
/* eslint-enable no-template-curly-in-string */

// --- Components ---

const DebounceSelect = ({ fetchOptions, debounceTimeout = 800, ...props }) => {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState([]);
    const fetchRef = useRef(0);

    const debounceFetcher = React.useMemo(() => {
        const loadOptions = (value) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);

            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    return; // for fetch callback order
                }
                setOptions(newOptions);
                setFetching(false);
            });
        };
        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    return (
        <Select
            labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            {...props}
            options={options}
        />
    );
};

const ParticipantsPage = () => {
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
    const { makeCall, status, inCall, isCalling } = useSoftphone();
    const softphoneEnabled = status === 'registered';
    const canCall = softphoneEnabled && !inCall && !isCalling;

    // --- State ---
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        search: '',
        ubigeo_region: null,
        group_id: null,
        uuid: null,
        phone: ''
    });
    const [filterOptions, setFilterOptions] = useState({ regions: [], groups: [] });

    // Modals & Drawers State
    const [participantModal, setParticipantModal] = useState({ visible: false, mode: 'create', data: null });
    const [detailsDrawer, setDetailsDrawer] = useState({ visible: false, data: null });
    const [cardsDrawer, setCardsDrawer] = useState({ visible: false, participant: null, cards: [], loading: false });
    const [cardModal, setCardModal] = useState({ visible: false, mode: 'create', data: null });

    const [form] = Form.useForm();
    const [cardForm] = Form.useForm();
    const [groupsDrawerVisible, setGroupsDrawerVisible] = useState(false);
    const [tagsDrawerVisible, setTagsDrawerVisible] = useState(false);

    // --- Data Fetching ---

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await participantService.list(filters);
            if (response.status === 'success') {
                setData(response.data);
                setTotal(response.total);
            }
        } catch (error) {
            message.error('Error al cargar participantes');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchFilters = useCallback(async () => {
        try {
            const [filtersRes, tagsRes] = await Promise.all([
                participantService.getFilters(),
                participantService.getCardTags()
            ]);

            setFilterOptions({
                regions: filtersRes.data.regions || [],
                groups: filtersRes.data.groups || [],
                tags: tagsRes.data || []
            });
        } catch (error) {
            console.error('Error fetching filters', error);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchFilters();
    }, [fetchFilters]);

    // --- Handlers ---

    const handleSearch = (value) => setFilters(prev => ({ ...prev, search: value, uuid: null, page: 1 }));
    const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value, uuid: null, page: 1 }));
    const handleTableChange = (pagination) => setFilters(prev => ({ ...prev, page: pagination.current, limit: pagination.pageSize }));

    // Participant CRUD
    const openParticipantModal = async (mode, record = null) => {
        form.resetFields();
        if (mode === 'edit' && record) {
            try {
                // Fetch full details to get phones and groups properly
                const detail = await participantService.getDetail(record.uuid);
                // Prepare form data
                const formData = {
                    ...detail.data,
                    ubigeo_code: detail.data.ubigeo_code ? {
                        label: detail.data.ubigeo_text,
                        value: detail.data.ubigeo_code
                    } : null,
                    phones: detail.data.phones && detail.data.phones.length > 0
                        ? detail.data.phones.map(p => ({ number: p }))
                        : [{ number: '' }],
                    groups: detail.data.group_ids,
                    registry_date: detail.data.registry_date ? dayjs(detail.data.registry_date) : null
                };
                form.setFieldsValue(formData);
                setParticipantModal({ visible: true, mode, data: detail.data });
            } catch (e) {
                message.error('Error al cargar detalles para editar');
            }
        } else {
            form.setFieldsValue({ 
                phones: [{ number: '' }],
                registry_date: dayjs()
            });
            setParticipantModal({ visible: true, mode: 'create', data: null });
        }
    };

    const handleParticipantSubmit = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                ubigeo_code: values.ubigeo_code ? values.ubigeo_code.value : null,
                registry_date: values.registry_date ? values.registry_date.format('YYYY-MM-DD') : null
            };

            if (participantModal.mode === 'create') {
                await participantService.create(payload);
                message.success('Participante creado exitosamente');
            } else {
                payload.uuid = participantModal.data.uuid;
                await participantService.update(payload);
                message.success('Participante actualizado exitosamente');
            }
            setParticipantModal({ ...participantModal, visible: false });
            fetchData();
        } catch (error) {
            if (error.errorFields) return; // Form validation error
            message.error('Operación fallida: ' + (error.message || 'Error desconocido'));
        }
    };

    const handleDeleteParticipant = async (uuid) => {
        try {
            await participantService.delete(uuid);
            message.success('Participante eliminado');
            fetchData();
        } catch (e) {
            message.error('Error al eliminar participante');
        }
    };

    // Details Drawer
    const openDetailsDrawer = async (record) => {
        try {
            const detail = await participantService.getDetail(record.uuid);
            setDetailsDrawer({ visible: true, data: detail.data });
        } catch (e) {
            message.error('Error al cargar detalles');
        }
    };

    // Cards Logic
    const loadCards = async (participantUuid) => {
        setCardsDrawer(prev => ({ ...prev, loading: true }));
        try {
            const res = await participantService.getCards(participantUuid);
            setCardsDrawer(prev => ({ ...prev, cards: res.data || [], loading: false }));
        } catch (e) {
            message.error('Error al cargar tarjetas');
            setCardsDrawer(prev => ({ ...prev, loading: false }));
        }
    };

    const openCardsDrawer = (record) => {
        setCardsDrawer({ visible: true, participant: record, cards: [], loading: true });
        loadCards(record.uuid);
    };

    const handleCardSubmit = async () => {
        try {
            const values = await cardForm.validateFields();
            const payload = { ...values, participant_uuid: cardsDrawer.participant.uuid };

            if (cardModal.mode === 'create') {
                await participantService.createCard(payload);
                message.success('Tarjeta agregada');
            } else {
                payload.id = cardModal.data.id;
                await participantService.updateCard(payload);
                message.success('Tarjeta actualizada');
            }
            setCardModal({ ...cardModal, visible: false });
            loadCards(cardsDrawer.participant.uuid);
        } catch (e) {
            if (e.errorFields) return;
            message.error('Error al guardar tarjeta');
        }
    };

    const handleDeleteCard = async (id) => {
        try {
            await participantService.deleteCard(id);
            message.success('Tarjeta eliminada');
            // Optimistic update or reload
            loadCards(cardsDrawer.participant.uuid);
        } catch (e) {
            message.error('Error al eliminar tarjeta');
        }
    };

    // --- Ubigeo Search ---
    const fetchUbigeo = async (search) => {
        try {
            const res = await participantService.searchUbigeo(search || '');
            if (Array.isArray(res.data)) {
                return res.data.map(u => ({
                    label: u.text,
                    value: u.code
                }));
            }
            return [];
        } catch (e) {
            console.error("Ubigeo Search Error", e);
            return [];
        }
    };

    // --- Columns ---
    const columns = [
        {
            title: 'Código hogar',
            dataIndex: 'homecode',
            key: 'homecode',
            width: 120,
            render: (text, record) => (
                <Text
                    strong
                    style={{ color: PRIMARY_RED, cursor: 'pointer' }}
                    onClick={() => openDetailsDrawer(record)}
                >
                    {text || '-'}
                </Text>
            )
        },
        {
            title: 'Nombres',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Fecha de registro',
            dataIndex: 'registry_date',
            key: 'registry_date',
            width: 130,
            render: (date) => date || '-'
        },
        {
            title: 'Documento',
            dataIndex: 'document_number',
            key: 'document',
            render: (text, record) => (
                <Tag color="blue">{record.document_type} - {text}</Tag>
            )
        },
        {
            title: 'Edad',
            dataIndex: 'age',
            key: 'age',
            width: 80,
            render: (age) => age || '-'
        },
        {
            title: 'Nacionalidad',
            dataIndex: 'nationality',
            key: 'nationality',
            render: (nat) => nat || '-'
        },
        {
            title: 'Teléfonos',
            dataIndex: 'phone_numbers',
            key: 'phone_numbers',
            render: (phones) => (
                <div style={{ whiteSpace: 'pre-line' }}>
                    {phones ? phones.split(',').join('\n') : '-'}
                </div>
            )
        },
        {
            title: 'Ubigeo',
            key: 'ubigeo',
            render: (_, record) => record.ubigeo_text || '-'
        },
        {
            title: 'Grupos',
            dataIndex: 'group_names',
            key: 'groups',
            render: (groups) => (
                <Space size={[0, 4]} wrap>
                    {groups ? groups.split(',').map(g => (
                        <Tag key={g} color="cyan" style={{ borderRadius: '10px' }}>{g.trim()}</Tag>
                    )) : <Text type="secondary" style={{ fontSize: '11px' }}>Sin grupos</Text>}
                </Space>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, record) => {
                const items = [
                    {
                        key: 'edit',
                        label: 'Editar',
                        icon: <EditOutlined />,
                        onClick: () => openParticipantModal('edit', record)
                    },
                    {
                        key: 'cards',
                        label: 'Ver Tarjetas',
                        icon: <IdcardOutlined />,
                        onClick: () => openCardsDrawer(record)
                    },
                    {
                        key: 'details',
                        label: 'Ver Detalles',
                        icon: <UserOutlined />,
                        onClick: () => openDetailsDrawer(record)
                    },
                    {
                        type: 'divider'
                    },
                    {
                        key: 'delete',
                        label: 'Eliminar',
                        icon: <DeleteOutlined />,
                        danger: true,
                    }
                ];

                return (
                    <Dropdown
                        menu={{
                            items,
                            onClick: ({ key }) => {
                                if (key === 'delete') {
                                    Modal.confirm({
                                        title: '¿Estás seguro?',
                                        content: `Eliminarás al participante ${record.name}. Esta acción no se puede deshacer.`,
                                        okText: 'Sí, Eliminar',
                                        okType: 'danger',
                                        cancelText: 'Cancelar',
                                        onOk: () => handleDeleteParticipant(record.uuid)
                                    });
                                }
                            }
                        }}
                        trigger={['click']}
                    >
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            }
        }
    ];

    return (
        <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Gestión de Participantes</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    style={{ background: PRIMARY_RED, borderColor: PRIMARY_RED }}
                    onClick={() => openParticipantModal('create')}
                >
                    Nuevo Participante
                </Button>
            </div>

            {/* Filters */}
            <Card bodyStyle={{ padding: '16px' }}>
                <Space wrap size="middle">
                    <Input.Search
                        placeholder="Buscar por nombre o documento..."
                        onSearch={handleSearch}
                        style={{ width: 250 }}
                        allowClear
                    />
                    <Input.Search
                        placeholder="Buscar por teléfono..."
                        onSearch={(val) => handleFilterChange('phone', val)}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Select
                        placeholder="Filtrar por Región"
                        style={{ width: 200 }}
                        onChange={(val) => handleFilterChange('ubigeo_region', val)}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                        <Option value={null}>Todos los departamentos</Option>
                        {filterOptions.regions.map(r => (
                            <Option key={r} value={r}>{r}</Option>
                        ))}
                    </Select>
                    <Select
                        placeholder="Filtrar por Grupo"
                        style={{ width: 220 }}
                        onChange={(val) => handleFilterChange('group_id', val)}
                        value={filters.group_id}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                        <Option value={null}>Todos los grupos</Option>
                        {filterOptions.groups.map(g => (
                            <Option key={g.id} value={g.id}>{g.name}</Option>
                        ))}
                    </Select>

                    {permissions.includes('participants.groups.view') && (
                        <Button icon={<FilterOutlined />} onClick={() => setGroupsDrawerVisible(true)}>Grupos</Button>
                    )}
                    {permissions.includes('participants.tags.view') && (
                        <Button icon={<SearchOutlined />} onClick={() => setTagsDrawerVisible(true)}>Tags</Button>
                    )}
                </Space>
            </Card>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: filters.page,
                    pageSize: filters.limit,
                    total: total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    locale: { items_per_page: '' },
                    showTotal: (total, range) => `${total} resultados`
                }}
                onChange={handleTableChange}
                scroll={{ x: 1000 }}
            />

            {/* --- Modals & Drawers --- */}

            {/* Participant Modal (Create/Edit) */}
            <Modal
                title={participantModal.mode === 'create' ? "Nuevo Participante" : "Editar Participante"}
                open={participantModal.visible}
                onOk={handleParticipantSubmit}
                onCancel={() => setParticipantModal({ ...participantModal, visible: false })}
                width={700}
                centered
            >
                <Form form={form} layout="vertical" validateMessages={validateMessages}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Código hogar" name="homecode" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} placeholder="Ej. 1234567890" maxLength={10} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Fecha de registro" name="registry_date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Género" name="gender">
                                <Select placeholder="Seleccionar" allowClear>
                                    <Option value="M">Masculino</Option>
                                    <Option value="F">Femenino</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Nombres Completos" name="name" rules={[{ required: true }]}>
                        <Input placeholder="Ej. Juan Pérez" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Space style={{ display: 'flex', width: '100%' }} align="start">
                                <Form.Item label="Tipo Doc." name="document_type" rules={[{ required: true }]} style={{ width: 120 }}>
                                    <Select>
                                        <Option value="DNI">DNI</Option>
                                        <Option value="CE">CE</Option>
                                        <Option value="PA">PASAPORTE</Option>
                                        <Option value="RUC">RUC</Option>
                                        <Option value="CPP">CPP</Option>
                                        <Option value="Otro">Otro</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="N° Documento" name="document_number" rules={[{ required: true }]} style={{ flex: 1 }}>
                                    <Input placeholder="Número de documento" />
                                </Form.Item>
                            </Space>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Edad" name="age">
                                <InputNumber min={0} max={120} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Nacionalidad" name="nationality">
                                <Input placeholder="Ej. Peruano" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Correo Electrónico" name="email" rules={[{ type: 'email' }]}>
                                <Input placeholder="nombre@correo.com" prefix={<UserOutlined />} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Tarjeta" name="card_number">
                                <Input placeholder="Número de tarjeta" prefix={<CreditCardOutlined />} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Dirección" name="address">
                        <Input placeholder="Dirección de residencia" />
                    </Form.Item>

                        <Form.Item label="Ubigeo (Departamento - Provincia - Distrito)" name="ubigeo_code" rules={[{ required: true, message: 'El ubigeo es obligatorio' }]}>
                            <DebounceSelect
                                showSearch
                                placeholder="Buscar o seleccionar distrito..."
                                fetchOptions={fetchUbigeo}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>

                    <Form.List name="phones">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Space key={field.key} align="baseline" style={{ display: 'flex' }}>
                                        <Form.Item
                                            {...field}
                                            label={index === 0 ? "Teléfonos" : ""}
                                            name={[field.name, 'number']}
                                            rules={[
                                                { required: true, message: 'Falta número' },
                                                { pattern: /^\d{7,}$/, message: 'Mínimo 7 dígitos' }
                                            ]}
                                        >
                                            <Input placeholder="51999888777" prefix={<PhoneOutlined />} />
                                        </Form.Item>
                                        {fields.length > 1 ? (
                                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                        ) : null}
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                                        Agregar Teléfono
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item label="Grupos" name="groups">
                        <Select
                            mode="multiple"
                            placeholder="Seleccione grupos"
                            optionFilterProp="children"
                            filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                        >
                            {filterOptions.groups.map(g => (
                                <Option key={g.id} value={g.id}>{g.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Details Drawer */}
            <Drawer
                title="Detalles del Participante"
                placement="right"
                onClose={() => setDetailsDrawer({ ...detailsDrawer, visible: false })}
                open={detailsDrawer.visible}
                width={500}
            >
                {detailsDrawer.data ? (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div style={{ textAlign: 'center', marginBottom: 20 }}>
                            <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: PRIMARY_RED }} />
                            <Title level={4} style={{ marginTop: 10 }}>{detailsDrawer.data.name}</Title>
                            <Tag color="geekblue">{detailsDrawer.data.document_type} {detailsDrawer.data.document_number}</Tag>
                        </div>

                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Código hogar"><Text strong>{detailsDrawer.data.homecode || '-'}</Text></Descriptions.Item>
                            <Descriptions.Item label="Fecha de registro">{detailsDrawer.data.registry_date || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Género">{detailsDrawer.data.gender === 'M' ? 'Masculino' : (detailsDrawer.data.gender === 'F' ? 'Femenino' : '-')}</Descriptions.Item>
                            <Descriptions.Item label="Edad">{detailsDrawer.data.age || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Nacionalidad">{detailsDrawer.data.nationality || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Email">{detailsDrawer.data.email || 'No registrado'}</Descriptions.Item>
                            <Descriptions.Item label="Tarjeta">{detailsDrawer.data.card_number || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Dirección">{detailsDrawer.data.address || 'No registrada'}</Descriptions.Item>
                            <Descriptions.Item label="Ubigeo">
                                {detailsDrawer.data.ubigeo_code ? `[${detailsDrawer.data.ubigeo_code}] ${detailsDrawer.data.ubigeo_text}` : 'No registrado'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Grupos">
                                {detailsDrawer.data.group_names ? detailsDrawer.data.group_names.split(',').map(g => (
                                    <Tag key={g}>{g.trim()}</Tag>
                                )) : '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left"><PhoneOutlined /> Teléfonos</Divider>
                        <List
                            size="small"
                            bordered
                            dataSource={detailsDrawer.data.phones}
                            renderItem={item => (
                                <List.Item
                                    extra={
                                        <Tooltip title={!softphoneEnabled ? "Softphone no configurado o desconectado" : (inCall || isCalling ? "Llamada en curso" : "Llamar")}>
                                            <Button
                                                type="primary"
                                                shape="circle"
                                                icon={<PhoneOutlined />}
                                                size="small"
                                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                disabled={!canCall}
                                                onClick={() => {
                                                    message.info(`Llamando a ${item}...`);
                                                    makeCall(item);
                                                }}
                                            />
                                        </Tooltip>
                                    }
                                >
                                    <Text strong>{item}</Text>
                                </List.Item>
                            )}
                        />
                    </Space>
                ) : <Spin />}
            </Drawer>

            {/* Cards Management Drawer */}
            <Drawer
                title={`Tarjetas de ${cardsDrawer.participant?.name || ''}`}
                placement="right"
                width={600}
                onClose={() => setCardsDrawer({ ...cardsDrawer, visible: false })}
                open={cardsDrawer.visible}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            cardForm.resetFields();
                            setCardModal({ visible: true, mode: 'create', data: null });
                        }}
                    >
                        Nueva Tarjeta
                    </Button>
                }
            >
                {/* List of Cards */}
                <List
                    loading={cardsDrawer.loading}
                    dataSource={cardsDrawer.cards}
                    locale={{ emptyText: "No hay tarjetas registradas." }}
                    renderItem={item => (
                        <div style={{
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '16px',
                            backgroundColor: '#fff'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <Text strong style={{ fontSize: '16px' }}>{item.title}</Text>
                                <Space>
                                    <Button
                                        type="text"
                                        style={{ backgroundColor: '#e6f7ff', color: '#1890ff', border: 'none' }}
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            // Map tags to just IDs for the select
                                            const tags = item.tags ? item.tags.map(t => t.id) : [];
                                            cardForm.setFieldsValue({ ...item, tags });
                                            setCardModal({ visible: true, mode: 'edit', data: item });
                                        }}
                                    />
                                    <Popconfirm
                                        title="¿Estás seguro de que deseas eliminar esta tarjeta?"
                                        okText="Sí, Eliminar"
                                        cancelText="Cancelar"
                                        okType="danger"
                                        onConfirm={() => handleDeleteCard(item.id)}
                                    >
                                        <Button
                                            type="text"
                                            danger
                                            style={{ backgroundColor: '#fff1f0', border: 'none' }}
                                            icon={<DeleteOutlined />}
                                        />
                                    </Popconfirm>
                                </Space>
                            </div>
                            <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>{item.description}</Text>
                            <Space size={[0, 8]} wrap>
                                {item.tags && item.tags.map(tag => (
                                    <Tag key={tag.id} color={tag.color || 'blue'}>{tag.name}</Tag>
                                ))}
                            </Space>
                        </div>
                    )}
                />

                {/* Card Modal (Nested) */}
                <Modal
                    title={cardModal.mode === 'create' ? "Nueva Tarjeta" : "Editar Tarjeta"}
                    open={cardModal.visible}
                    onOk={handleCardSubmit}
                    onCancel={() => setCardModal({ ...cardModal, visible: false })}
                    okText="Guardar"
                    cancelText="Cancelar"
                >
                    <Form form={cardForm} layout="vertical">
                        <Form.Item
                            label="Título"
                            name="title"
                            rules={[{ required: true, message: 'El título es obligatorio' }]}
                        >
                            <Input placeholder="Ej. Información de Salud" />
                        </Form.Item>

                        <Form.Item label="Descripción" name="description">
                            <Input.TextArea rows={4} placeholder="Detalles adicionales..." />
                        </Form.Item>

                        <Form.Item label="Tags" name="tags">
                            <Select
                                mode="multiple"
                                placeholder="Seleccionar etiquetas"
                                optionFilterProp="children"
                                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                            >
                                {filterOptions.tags && filterOptions.tags.map(t => (
                                    <Option key={t.id} value={t.id}>{t.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </Modal>
            </Drawer>

            {/* Groups Management (Drawer) */}
            <GroupsDrawer
                visible={groupsDrawerVisible}
                onClose={() => {
                    setGroupsDrawerVisible(false);
                    fetchFilters(); // Refresh group list for the filter select
                }}
                onSelectGroup={(groupId) => handleFilterChange('group_id', groupId)}
            />

            {/* Tags Management (Drawer) */}
            <TagsDrawer
                visible={tagsDrawerVisible}
                onClose={() => setTagsDrawerVisible(false)}
                onSelectParticipant={(participantUuid) => {
                    setFilters(prev => ({
                        ...prev,
                        search: '',
                        uuid: participantUuid,
                        page: 1
                    }));
                }}
            />
        </Space>
    );
};

export default ParticipantsPage;
